<template>
  <PForm
    ref="pForm"
    :heading="computedHeadingLabel"
    :primary-cta="ewayBillFormState.view ? {} : submitBtn"
    :secondary-cta="ewayBillFormState.view ? {} : secondaryCta"
    :show-footer="!ewayBillFormState.view"
    @close="onClose"
    @submit.prevent="onSubmit"
    @reset="resetForm"
  >
    <template v-if="ewayBillFormState.view" #header-info>
      <span v-if="ewayBillFormState.view">
        <PBtn class="q-pa-none" flat dense @click="openFilesDrawer">
          <DocumentIcon color="primary" class="icon--sm q-mr-xxs" />
          <span class="text-xs">
            Files ({{ formData?.file?.length || 0 }})
          </span>
        </PBtn>
      </span>
      <span class="q-mx-md">
        <PBadge
          :label="formData.status"
          :type="formData.status == 'Active' ? 'success' : 'Cancelled'"
        />
        <PTooltip
          v-if="formData.status === 'Cancelled' && formData?.reason?.length"
        >
          {{ formData.reason }}
        </PTooltip>
      </span>
    </template>
    <EWaybillUserDetails
      v-if="ewayBillFormState.view"
      :user-details="formData"
    />
    <div v-if="!ewayBillFormState.view" class="border-none q-ma-none q-pa-none">
      <div class="border-none q-ma-none q-pa-none">
        <div class="q-mb-md">
          <span class="q-mr-xxs">Status:</span>
          <span
            :class="
              formData.status_cd === 'active'
                ? 'text-info-50'
                : 'text-warning-70'
            "
          >
            {{ useChangeCase(formData.status_cd, "capitalCase").value || "-" }}
          </span>
        </div>
      </div>
    </div>
    <q-separator v-if="!ewayBillFormState.view" class="q-mb-xl q-mt-sm" />
    <div v-if="!ewayBillFormState.view" class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-4">
          <PInput
            v-model.trim="formData.number"
            label="E-Way Bill No."
            placeholder="Enter value"
            required
            :rules="[handleEWBNumberValidation]"
            :loading="validatingEWBNumber"
            debounce="300"
            :disable="Boolean(ewayBillFormState.preview)"
          />
        </div>
        <div class="col-4">
          <PVDateInput
            v-model="formData.date"
            label="E-Way Bill Date"
            placeholder="DD-MM-YYYY"
            required
            :disable="Boolean(ewayBillFormState.preview)"
            :max-date="currentDate"
          />
        </div>
        <div class="col-4">
          <PInput
            v-model.trim="formData.vehicle_number"
            label="Vehicle No."
            placeholder="Enter value"
            disable
          />
        </div>
        <div class="col-6">
          <div class="row">
            <div class="col-4">
              <span class="text-neutral-80">Invoice No.</span>
            </div>
            <div class="col-8">
              <span class="text-neutral text-wrap">
                {{ formData.invc_number || formData.invc_id?.number || "-" }}
              </span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">Invoice Date</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">
                {{ formatDate(formData.invc_date, "D MMM YYYY") }}
              </span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">Total Invoice Amount</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">
                {{ invoiceTotalAmount }}
              </span>
            </div>
          </div>
        </div>
        <div class="col-6">
          <div class="row">
            <div class="col-4">
              <span class="text-neutral-80">Customer</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">
                {{ formData.invc_id?.po_info?.buyer_name || "-" }}
              </span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">GSTIN</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">
                {{ formData.invc_id?.po_info?.buyer_gstin || "-" }}
              </span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">PO No.</span>
            </div>
            <div class="col-8">
              <span class="text-neutral text-wrap">{{
                formData.po_id?.number ||
                formData.invc_id?.po_info?.number ||
                "-"
              }}</span>
            </div>
            <div class="col-4">
              <span class="text-neutral-80">Poshn PO No.</span>
            </div>
            <div class="col-8">
              <span class="text-neutral">
                {{
                  formData.po_id?.internal_number ||
                  formData.invc_id?.po_info?.internal_number ||
                  "-"
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <q-separator class="q-mb-xl q-mt-sm" />
    <div v-if="!ewayBillFormState.view" class="border-none q-ma-none q-pa-none">
      <div class="row q-col-gutter-lg">
        <div class="col-6 column">
          <PInput
            v-model="formData.dispatch_address"
            class="col"
            label="Dispatch From"
            placeholder="Enter seller's billing address"
            type="textarea"
            autogrow
            required
            disable
          />
        </div>
        <div class="col-6 column">
          <PInput
            :model-value="shipping_address"
            class="col"
            label="Ship To"
            placeholder="Enter customer's shipping address"
            type="textarea"
            autogrow
            required
            disable
          />
        </div>
      </div>
    </div>
    <q-separator
      v-if="!ewayBillFormState.preview && !ewayBillFormState.view"
      class="q-mb-xl q-mt-sm"
    />
    <div v-if="ewayBillFormState?.view">
      <AssociatedVouchers :id="formData?._id" :type="'ewaybill'" />
    </div>
    <div
      v-if="!ewayBillFormState.preview && !ewayBillFormState.view"
      class="overflow-hidden"
    >
      <div class="row">
        <div class="col-12">
          <PFile
            v-model="file"
            label="Attach Proof of Document (E-Way Bill)"
            help="Allowed file types are PDFs, images, excel, csv"
            required
          />
          <PFile
            v-if="file"
            v-model="file"
            class="q-mt-xxs"
            variant="re-upload"
          />
        </div>
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { useQuasar, date } from "quasar";
import AutoGenIconUrl from "assets/icons/auto-gen.svg?url";
import { useChangeCase } from "@vueuse/integrations/useChangeCase";
import eWayBillFormModel from "src/modules/accounts/models/eWayBillForm";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import { numberFormat } from "assets/scripts/utilities/helpers";
import { notify } from "assets/scripts/utilities/api-clients";
import { useCheckDuplicateEWBNumber } from "src/composables/checkDuplicateEWBNumber";
import AssociatedVouchers from "src/modules/purchase-bills/components/AssociatedVouchers.vue";
import DocumentIcon from "assets/icons/document.svg";

const $q = useQuasar();

const { formatDate } = date;

const pForm = ref(null);
const currentDate = new Date();
const { viewFile } = useFile();
const { toggleAssociatedDataDrawer } = usePaymentRequestStore();
const { removeQueryParams } = useRouterUtilities();

const status = useColor();

const { validateEWBNumber, validatingEWBNumber, isDuplicateEWBNumber } =
  useCheckDuplicateEWBNumber();

const { ewayBillFormState, eWayBill } = storeToRefs(useEWayBillStore());
const {
  autoGenerateEWayBill,
  getEWayBills,
  postEWayBill,
  putEWayBill,
  toggleEWayBillForm,
} = useEWayBillStore();

const { formData, isLoading, closeForm, resetForm, submitForm } = useForm({
  formModel: eWayBillFormModel(),
  entity: eWayBill,
  formRef: pForm,
  isForm: ewayBillFormState,
  post: postEWayBill,
  put: putEWayBill,
  toggleForm: toggleEWayBillForm,
});

const onClose = () => {
  removeQueryParams();

  closeForm();
};
const invoiceTotalAmount = computed(() => {
  let totalAmount = 0;

  if (unref(formData).invc_total_amount) {
    totalAmount = unref(formData).invc_total_amount;
  }

  let invoice = unref(formData).invc_id;

  if (invoice?.stage_cd !== "pending") {
    totalAmount += invoice?.tcs?.amount || 0;
  }

  return numberFormat({
    number: totalAmount,
    style: "currency",
  });
});

const shipping_address = computed(() => {
  if (unref(formData)?.shipping_address) {
    const shop_name = unref(formData)?.shipping_address_info?.shop_name
      ? `${unref(formData)?.shipping_address_info?.shop_name}\n`
      : "";

    return `${shop_name}${unref(formData)?.shipping_address}`;
  }

  return "";
});

const submitBtn = computed(() => {
  if (unref(ewayBillFormState).preview) {
    return {};
  }

  return {
    loading: unref(isLoading),
    disable: unref(validatingEWBNumber) || unref(isDuplicateEWBNumber),
  };
});

let isAutoGenerating = ref(false);

const onConfirmAutoGeneration = async () => {
  try {
    isAutoGenerating.value = true;

    await autoGenerateEWayBill(unref(formData));

    closeForm(true);
  } catch (error) {
    console.error(error);
  } finally {
    isAutoGenerating.value = false;
  }
};

const confirmAutoGeneration = () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      labelClass: "text-center q-ma-xl rounded-borders",
      text: "Are you sure want to proceed?",
      onConfirm: onConfirmAutoGeneration,
    },
  });
};

const oneClickGenerateButton = computed(() => {
  return {
    disable: unref(validatingEWBNumber) || unref(isDuplicateEWBNumber),
    icon: `img:${AutoGenIconUrl}`,
    label: "1-Click generate",
    loading: unref(isAutoGenerating),
    onClick: confirmAutoGeneration,
    outline: true,
    type: "button",
  };
});

const secondaryCta = computed(() => {
  if (unref(ewayBillFormState).preview) {
    return {};
  }

  if (unref(formData).stage === "Not Started") {
    return unref(oneClickGenerateButton);
  }

  return { visible: true };
});

const { getCounts } = useAccountStore();
const { file, uploadFile } = useFile(formData);

const computedHeadingLabel = computed(() => {
  const state = unref(ewayBillFormState);
  const bill = unref(eWayBill);

  if (state?.view) {
    return bill?.number || "-";
  } else if (state?.preview) {
    return "View E-Way Bill";
  } else {
    return "Add E-Way Bill";
  }
});

const onSubmit = async () => {
  const submission = await submitForm(false);

  isLoading.value = true;

  await uploadFile({ type: "E-Way Bill", showMessage: false });

  await Promise.all([getCounts(), getEWayBills()]);

  isLoading.value = false;

  closeForm(true);

  notify({
    type: "positive",
    message: submission?.data?.message,
  });
};

const handleEWBNumberValidation = async (number) => {
  return await validateEWBNumber({
    number,
    _id: unref(formData)?._id,
  });
};

const filesColumns = computed(() => {
  return [
    {
      align: "left",
      label: "Date",
      field: "created_date",
      format: (value) => {
        return formatDate(value, "DD MMM YYYY");
      },
    },
    {
      align: "left",
      label: "Name",
      field: "file_name",
      cellClass: "text-primary text-weight-medium underline cursor-pointer",
      onClick: viewFile,
    },
    {
      align: "left",
      label: "Status",
      field: "status",
      format: (value) =>
        `<span class="bg-${status(value).color} text-${
          status(value).textColor
        } q-px-sm q-py-xxs rounded" title="Status">${value}</span>`,
      cellClass: "q-px-sm q-py-xxs rounded",
    },
  ];
});

const openFilesDrawer = () => {
  toggleAssociatedDataDrawer({
    edit: false,
    level: 0,
    open: true,
    view: true,
    props: {
      columns: unref(filesColumns),
      rows: formData.value?.file,
      heading: "Associated Files",
      type: "file",
    },
  });
};
</script>
